<template>
  <div class="device-detail">
    <el-page-header @back="goBack" content="设备详情" title="返回">
      <template #extra>
        <el-button @click="refreshDevice" :loading="loading">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </template>
    </el-page-header>

    <el-card v-loading="loading" style="margin-top: 20px;">
      <template #header>
        <div class="card-header">
          <span>基本信息</span>
          <el-tag :type="device.armStatus === 'armed' ? 'success' : 'info'">
            {{ device.armStatus === 'armed' ? '已布防' : '未布防' }}
          </el-tag>
        </div>
      </template>

      <el-descriptions :column="2" border>
        <el-descriptions-item label="设备名称">{{ device.deviceName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="设备ID">{{ device.deviceId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="设备型号">{{ device.deviceModel || device.productModel || '-' }}</el-descriptions-item>
        <el-descriptions-item label="产品名称">{{ device.productName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="所属家庭">{{ device.familyName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="安装位置">{{ device.location || device.locationInfo || '-' }}</el-descriptions-item>
        <el-descriptions-item label="在线状态">
          <el-tag :type="getStatusType(device.status)">
            {{ getStatusText(device.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="电池电量">
          <el-progress
            :percentage="device.batteryLevel || 0"
            :color="getBatteryColor(device.batteryLevel)"
            :format="() => `${device.batteryLevel || 0}%`"
          />
        </el-descriptions-item>
        <el-descriptions-item label="信号强度">
          <span v-if="device.signalStrength">
            {{ device.signalStrength }} ({{ formatSignalStrength(device.signalStrength) }})
            <el-tag :type="getSignalQualityType(device.signalStrength)" size="small" style="margin-left: 8px;">
              {{ getSignalQualityText(device.signalStrength) }}
            </el-tag>
          </span>
          <span v-else>-</span>
        </el-descriptions-item>
        <el-descriptions-item label="固件版本">{{ device.firmwareVersion || '-' }}</el-descriptions-item>
        <el-descriptions-item label="最后上线时间" :span="2">
          {{ device.lastHeartbeat ? formatDateTime(device.lastHeartbeat) : (device.lastOnlineTime ? formatDateTime(device.lastOnlineTime) : '-') }}
        </el-descriptions-item>
        <el-descriptions-item label="创建时间" :span="2">
          {{ device.createdAt ? formatDateTime(device.createdAt) : '-' }}
        </el-descriptions-item>
        <el-descriptions-item label="设备描述" :span="2">
          {{ device.description || '-' }}
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 设备控制卡片 -->
    <el-card style="margin-top: 20px;">
      <template #header>
        <div class="card-header">
          <span>设备控制</span>
          <el-tag type="warning" v-if="!device.productId">未关联产品，无法发送指令</el-tag>
        </div>
      </template>

      <el-alert
        title="设备控制说明"
        type="info"
        :closable="false"
        style="margin-bottom: 20px;"
      >
        <p>通过电信AEP平台向设备发送控制指令。设备必须关联产品且产品已配置AEP_PRODUCT_ID和指令参数。</p>
      </el-alert>

      <el-space wrap :size="20">
        <el-button
          type="warning"
          @click="muteDevice"
          :loading="muteLoading"
          :disabled="!device.productId"
          size="large"
        >
          <el-icon><Bell /></el-icon>
          消音
        </el-button>

        <el-button
          type="danger"
          @click="resetDevice"
          :loading="resetLoading"
          :disabled="!device.productId"
          size="large"
        >
          <el-icon><RefreshRight /></el-icon>
          复位
        </el-button>

        <el-button
          type="primary"
          @click="queryDevice"
          :loading="queryLoading"
          :disabled="!device.productId"
          size="large"
        >
          <el-icon><Search /></el-icon>
          查询状态
        </el-button>
      </el-space>

      <!-- 指令执行结果 -->
      <el-card v-if="commandResult" style="margin-top: 20px;" shadow="never">
        <template #header>
          <span>指令执行结果</span>
        </template>
        <el-result
          :icon="commandResult.success ? 'success' : 'error'"
          :title="commandResult.message"
        >
          <template #extra>
            <el-descriptions :column="1" border size="small">
              <el-descriptions-item label="设备ID">{{ commandResult.deviceId }}</el-descriptions-item>
              <el-descriptions-item label="设备名称">{{ commandResult.deviceName }}</el-descriptions-item>
              <el-descriptions-item label="执行时间">{{ formatDateTime(new Date()) }}</el-descriptions-item>
              <el-descriptions-item label="详细信息" v-if="commandResult.commandResult">
                <pre style="margin: 0;">{{ JSON.stringify(commandResult.commandResult, null, 2) }}</pre>
              </el-descriptions-item>
            </el-descriptions>
          </template>
        </el-result>
      </el-card>
    </el-card>

    <!-- 产品配置信息 -->
    <el-card v-if="productConfig" style="margin-top: 20px;">
      <template #header>
        <span>产品AEP配置</span>
      </template>
      <el-descriptions :column="2" border size="small">
        <el-descriptions-item label="产品名称">{{ productConfig.productName }}</el-descriptions-item>
        <el-descriptions-item label="AEP产品ID">{{ productConfig.aepProductId || '未配置' }}</el-descriptions-item>
        <el-descriptions-item label="消音指令" :span="2">
          <pre style="margin: 0;">{{ formatCommand(productConfig.muteCommand) }}</pre>
        </el-descriptions-item>
        <el-descriptions-item label="复位指令" :span="2">
          <pre style="margin: 0;">{{ formatCommand(productConfig.resetCommand) }}</pre>
        </el-descriptions-item>
        <el-descriptions-item label="查询指令" :span="2">
          <pre style="margin: 0;">{{ formatCommand(productConfig.queryCommand) }}</pre>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Refresh, Bell, RefreshRight, Search } from '@element-plus/icons-vue'
import api from '@/utils/api'

export default {
  name: 'DeviceDetail',
  components: {
    Refresh, Bell, RefreshRight, Search
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const loading = ref(false)
    const muteLoading = ref(false)
    const resetLoading = ref(false)
    const queryLoading = ref(false)
    const device = ref({})
    const productConfig = ref(null)
    const commandResult = ref(null)

    const deviceId = route.params.id

    // 加载设备详情
    const loadDevice = async () => {
      loading.value = true
      try {
        const response = await api.get(`/admin/devices/${deviceId}`)
        if (response.success) {
          device.value = response.data

          // 如果设备关联了产品，加载产品配置
          if (device.value.productId) {
            await loadProductConfig(device.value.productId)
          }
        } else {
          ElMessage.error(response.message || '加载设备信息失败')
        }
      } catch (error) {
        console.error('加载设备信息失败:', error)
        ElMessage.error('加载设备信息失败')
      } finally {
        loading.value = false
      }
    }

    // 加载产品配置
    const loadProductConfig = async (productId) => {
      try {
        const response = await api.get(`/products/${productId}`)
        if (response.success && response.data) {
          const product = response.data

          // 使用后端提供的配置字段
          let aepProductId = product.aepProductId
          let muteCommand = product.muteCommandConfig
          let resetCommand = product.resetCommandConfig
          let queryCommand = product.queryCommandConfig

          // 如果后端字段为空，尝试从specifications解析（向后兼容）
          if (!aepProductId && product.specifications) {
            try {
              const specs = JSON.parse(product.specifications)
              aepProductId = specs.AEP_PRODUCT_ID
              if (specs.command) {
                muteCommand = muteCommand || specs.command.mute
                resetCommand = resetCommand || specs.command.reset
                queryCommand = queryCommand || specs.command.query
              }
            } catch (error) {
              console.error('解析产品配置失败:', error)
            }
          }

          productConfig.value = {
            productName: product.name,
            aepProductId,
            muteCommand,
            resetCommand,
            queryCommand
          }
        }
      } catch (error) {
        console.error('加载产品配置失败:', error)
      }
    }

    // 消音设备
    const muteDevice = async () => {
      try {
        await ElMessageBox.confirm(
          `确定要对设备 "${device.value.deviceName}" 发送消音指令吗？`,
          '设备消音',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        muteLoading.value = true
        commandResult.value = null

        const response = await api.post(`/devices/${device.value.deviceId}/mute`, {})

        if (response.success) {
          commandResult.value = response
          ElMessage.success('消音指令发送成功')
        } else {
          commandResult.value = response
          ElMessage.error(response.message || '消音指令发送失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('消音操作失败:', error)
          ElMessage.error('消音操作失败')
        }
      } finally {
        muteLoading.value = false
      }
    }

    // 复位设备
    const resetDevice = async () => {
      try {
        await ElMessageBox.confirm(
          `确定要对设备 "${device.value.deviceName}" 发送复位指令吗？复位后设备将重启。`,
          '设备复位',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        resetLoading.value = true
        commandResult.value = null

        const response = await api.post(`/devices/${device.value.deviceId}/reset`, {})

        if (response.success) {
          commandResult.value = response
          ElMessage.success('复位指令发送成功')
        } else {
          commandResult.value = response
          ElMessage.error(response.message || '复位指令发送失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('复位操作失败:', error)
          ElMessage.error('复位操作失败')
        }
      } finally {
        resetLoading.value = false
      }
    }

    // 查询设备状态
    const queryDevice = async () => {
      queryLoading.value = true
      commandResult.value = null

      try {
        const response = await api.post(`/devices/${device.value.deviceId}/query`, {})

        if (response.success) {
          commandResult.value = response
          ElMessage.success('查询指令发送成功')
        } else {
          commandResult.value = response
          ElMessage.error(response.message || '查询指令发送失败')
        }
      } catch (error) {
        console.error('查询操作失败:', error)
        ElMessage.error('查询操作失败')
      } finally {
        queryLoading.value = false
      }
    }

    // 刷新设备信息
    const refreshDevice = () => {
      commandResult.value = null
      loadDevice()
    }

    // 返回上一页
    const goBack = () => {
      router.back()
    }

    // 格式化时间
    const formatDateTime = (dateTime) => {
      if (!dateTime) return '-'
      const date = new Date(dateTime)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }

    // 获取电池颜色
    const getBatteryColor = (level) => {
      if (level > 60) return '#67c23a'
      if (level > 30) return '#e6a23c'
      return '#f56c6c'
    }

    // 格式化指令
    const formatCommand = (command) => {
      if (!command) return '未配置'
      return JSON.stringify(command, null, 2)
    }

    // 获取状态类型（用于el-tag的type属性）
    const getStatusType = (status) => {
      const deviceStatus = Number(status)
      console.log('DeviceDetail - getStatusType - 原始值:', status, '类型:', typeof status, '转换后:', deviceStatus)
      if (deviceStatus === 1) return 'success'
      if (deviceStatus === 0) return 'danger'
      return 'info'
    }

    // 获取状态文本
    const getStatusText = (status) => {
      const deviceStatus = Number(status)
      console.log('DeviceDetail - getStatusText - 原始值:', status, '类型:', typeof status, '转换后:', deviceStatus)
      if (deviceStatus === 1) return '在线'
      if (deviceStatus === 0) return '离线'
      return '未知'
    }

    // 格式化信号强度 - RSRP值直接显示dBm
    const formatSignalStrength = (rsrp) => {
      if (!rsrp) return '无效值'
      return `${rsrp} dBm`
    }

    // 获取信号质量等级类型（用于el-tag的type属性）
    // RSRP标准范围：-44 dBm (优秀) 到 -140 dBm (较差)
    const getSignalQualityType = (rsrp) => {
      if (!rsrp) return 'info'
      const dbValue = Number(rsrp)
      // RSRP是负值，数值越大（越接近0）信号越好
      if (dbValue > -70) return 'success'       // 优秀
      if (dbValue >= -90) return 'success'     // 良好/可用
      if (dbValue >= -110) return 'warning'    // 一般/有干扰
      return 'danger'                           // 较差/干扰高
    }

    // 获取信号质量等级文本
    const getSignalQualityText = (rsrp) => {
      if (!rsrp) return '无效值'
      const dbValue = Number(rsrp)
      // RSRP是负值，数值越大（越接近0）信号越好
      if (dbValue > -70) return '优秀'
      if (dbValue >= -90) return '良好'
      if (dbValue >= -110) return '一般'
      return '较差'
    }

    onMounted(() => {
      loadDevice()
    })

    return {
      loading,
      muteLoading,
      resetLoading,
      queryLoading,
      device,
      productConfig,
      commandResult,
      loadDevice,
      muteDevice,
      resetDevice,
      queryDevice,
      refreshDevice,
      goBack,
      formatDateTime,
      getBatteryColor,
      formatCommand,
      getStatusType,
      getStatusText,
      formatSignalStrength,
      getSignalQualityType,
      getSignalQualityText
    }
  }
}
</script>

<style scoped>
.device-detail {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

pre {
  background-color: #f5f7fa;
  padding: 10px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  overflow-x: auto;
}
</style>
